import "./EditExpenseReportForm.css";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExpenseReport } from "../models/expense_report";
import { apiCall } from "../api/apiCall";

import { useTranslation } from "../i18n/UseTranslation";

type EditExpenseReportFormProps = {
  expenseReport: ExpenseReport;
};

function formatDateForInput(dateString?: string) {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().split("T")[0];
}

function EditExpenseReportForm({ expenseReport }: EditExpenseReportFormProps) {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const translations = t.editExpenseReport;

  const [supplierName, setSupplierName] = useState(
    expenseReport.supplier_name || "",
  );

  const [receiptNumber, setReceiptNumber] = useState(
    expenseReport.receipt_number || "",
  );

  const [expenseDate, setExpenseDate] = useState(
    formatDateForInput(expenseReport.expense_date?.toString()),
  );

  const [comment, setComment] = useState(expenseReport.comment || "");

  const [distanceKm, setDistanceKm] = useState(
    expenseReport.distance_km != null ? String(expenseReport.distance_km) : "",
  );

  const [vehiclePowerCv, setVehiclePowerCv] = useState(
    expenseReport.vehicle_power_cv != null
      ? String(expenseReport.vehicle_power_cv)
      : "",
  );

  const [vehicleType, setVehicleType] = useState(
    expenseReport.vehicle_type || "",
  );

  const [energyType, setEnergyType] = useState(expenseReport.energy_type || "");

  const [mileageRateYear, setMileageRateYear] = useState(
    expenseReport.mileage_rate_year != null
      ? String(expenseReport.mileage_rate_year)
      : "",
  );

  const [participantCount, setParticipantCount] = useState(
    expenseReport.participant_count != null
      ? String(expenseReport.participant_count)
      : "",
  );

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const payload = {
        supplier_name: supplierName.trim(),
        receipt_number: receiptNumber.trim(),
        expense_date: expenseDate,
        comment: comment.trim() || null,

        distance_km: distanceKm === "" ? null : Number(distanceKm),

        vehicle_power_cv: vehiclePowerCv === "" ? null : Number(vehiclePowerCv),

        vehicle_type: vehicleType.trim() || null,

        energy_type: energyType.trim() || null,

        mileage_rate_year:
          mileageRateYear === "" ? null : Number(mileageRateYear),

        participant_count:
          participantCount === "" ? null : Number(participantCount),
      };

      await apiCall(`/expense-reports/${expenseReport.uuid}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      alert(translations.success);

      navigate(`/expense-reports/${expenseReport.uuid}`);
    } catch (error) {
      console.error("Error updating expense report:", error);

      alert(translations.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-wrapper edit-expense-form-page">
      <form className="edit-expense-form-card" onSubmit={handleSubmit}>
        <div className="edit-expense-form-header">
          <div>
            <h1>{translations.title}</h1>
            <p>{translations.subtitle}</p>
          </div>
        </div>

        <div className="edit-expense-form-grid">
          <div className="edit-expense-form-group">
            <label htmlFor="supplier-name">
              {translations.fields.supplierName}
            </label>

            <input
              id="supplier-name"
              type="text"
              value={supplierName}
              onChange={(event) => setSupplierName(event.target.value)}
              placeholder={translations.placeholders.supplierName}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="receipt-number">
              {translations.fields.receiptNumber}
            </label>

            <input
              id="receipt-number"
              type="text"
              value={receiptNumber}
              onChange={(event) => setReceiptNumber(event.target.value)}
              placeholder={translations.placeholders.receiptNumber}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="expense-date">
              {translations.fields.expenseDate}
            </label>

            <input
              id="expense-date"
              type="date"
              value={expenseDate}
              onChange={(event) => setExpenseDate(event.target.value)}
              required
            />
          </div>

          <div className="edit-expense-form-group edit-expense-form-group-full">
            <label htmlFor="expense-comment">
              {translations.fields.comment}
            </label>

            <textarea
              id="expense-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={translations.placeholders.optionalComment}
              rows={4}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="distance-km">
              {translations.fields.distanceKm}
            </label>

            <input
              id="distance-km"
              type="number"
              min="0"
              step="0.01"
              value={distanceKm}
              onChange={(event) => setDistanceKm(event.target.value)}
              placeholder={translations.placeholders.zero}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="vehicle-power">
              {translations.fields.vehiclePowerCv}
            </label>

            <input
              id="vehicle-power"
              type="number"
              min="0"
              value={vehiclePowerCv}
              onChange={(event) => setVehiclePowerCv(event.target.value)}
              placeholder={translations.placeholders.zero}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="vehicle-type">
              {translations.fields.vehicleType}
            </label>

            <input
              id="vehicle-type"
              type="text"
              value={vehicleType}
              onChange={(event) => setVehicleType(event.target.value)}
              placeholder={translations.placeholders.vehicleType}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="energy-type">
              {translations.fields.energyType}
            </label>

            <input
              id="energy-type"
              type="text"
              value={energyType}
              onChange={(event) => setEnergyType(event.target.value)}
              placeholder={translations.placeholders.energyType}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="mileage-rate-year">
              {translations.fields.mileageRateYear}
            </label>

            <input
              id="mileage-rate-year"
              type="number"
              min="1900"
              value={mileageRateYear}
              onChange={(event) => setMileageRateYear(event.target.value)}
              placeholder={translations.placeholders.mileageRateYear}
            />
          </div>

          <div className="edit-expense-form-group">
            <label htmlFor="participant-count">
              {translations.fields.participantCount}
            </label>

            <input
              id="participant-count"
              type="number"
              min="0"
              value={participantCount}
              onChange={(event) => setParticipantCount(event.target.value)}
              placeholder={translations.placeholders.zero}
            />
          </div>
        </div>

        <div className="edit-expense-form-actions">
          <button
            type="button"
            className="secondary-btn"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            {translations.cancel}
          </button>

          <button type="submit" className="primary-btn" disabled={loading}>
            {loading ? translations.saving : translations.saveChanges}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditExpenseReportForm;
